import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { AuthorDisplay } from '@edusense/shared-ui';
import { CourseHeaderMeta } from './CourseHeaderMeta';
import { CourseHeaderPrice } from './CourseHeaderPrice';
import { CourseHeaderProgress } from './CourseHeaderProgress';
import { CourseHeaderActions } from './CourseHeaderActions';
import { Select as HeroSelect, SelectItem } from '@heroui/react';
const HeaderContainer = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;
const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  margin-bottom: 1rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1 1 auto;

  @media (max-width: 1024px) {
    gap: 1.25rem;
    width: 100%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;
const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 0 0 auto;

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: flex-start;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;
const AuthorWrapper = styled.div`
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;
const Divider = styled.div`
  height: 1px;
  background-color: #e5e7eb;
  margin: 1.5rem 0;
`;
const LanguageSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 180px;
  flex: 0 0 auto;

  @media (max-width: 1024px) {
    min-width: 0;
    width: 100%;
    justify-content: flex-end;
  }
`;
const LanguageValue = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;

  @media (max-width: 1024px) {
    flex: 1;
    text-align: right;
  }
`;
const ResponsiveSelect = styled(HeroSelect)`
  min-width: 180px;

  @media (max-width: 1024px) {
    flex: 1;
    min-width: 0;
  }
`;
export const CourseHeader = ({ courseData, priceData, isLoading, isEnrolled, isCompleted, showCancelButton, onEnrollmentSuccess, onCancelEnrollment, onReviewsClick, onAuthorClick, currentEnrollmentId, languageSelect, isInstructor, onEditClick })=>{
    console.log('[CourseHeader] Render with props:', {
        isEnrolled,
        isCompleted,
        showCancelButton,
        courseId: courseData?.id,
        courseTitle: courseData?.title
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(HeaderContainer, {
            children: /*#__PURE__*/ _jsx("div", {
                children: "로딩 중..."
            })
        });
    }
    // 무료 강좌 여부 확인
    const isFree = !priceData || priceData.amount === '0' || priceData.amount === '0.00';
    // Author 데이터 변환
    const authorForDisplay = {
        id: courseData.instructor.id,
        username: courseData.instructor.username,
        name: courseData.instructor.name || courseData.instructor.username,
        profileImage: courseData.instructor.profileImage
    };
    const languageSelectedKeys = useMemo(()=>languageSelect ? new Set([
            languageSelect.selected
        ]) : undefined, [
        languageSelect?.selected
    ]);
    const selectedLanguageLabel = useMemo(()=>{
        if (!languageSelect) {
            return null;
        }
        const matched = languageSelect.options.find((option)=>option.value === languageSelect.selected);
        return matched?.label ?? languageSelect.selected.toUpperCase();
    }, [
        languageSelect
    ]);
    const handleLanguageSelectionChange = useCallback((keys)=>{
        if (!languageSelect || keys === 'all') {
            return;
        }
        const next = Array.from(keys)[0];
        if (!next) {
            return;
        }
        const nextValue = String(next);
        if (nextValue !== languageSelect.selected) {
            languageSelect.onChange(nextValue);
        }
    }, [
        languageSelect
    ]);
    return /*#__PURE__*/ _jsxs(HeaderContainer, {
        children: [
            /*#__PURE__*/ _jsx(Title, {
                children: courseData.title
            }),
            /*#__PURE__*/ _jsxs(MetaRow, {
                children: [
                    /*#__PURE__*/ _jsxs(LeftSection, {
                        children: [
                            /*#__PURE__*/ _jsx(CourseHeaderMeta, {
                                rating: courseData.rating,
                                reviewCount: courseData.reviewCount,
                                enrollmentCount: courseData.enrollmentCount,
                                onReviewsClick: onReviewsClick
                            }),
                            /*#__PURE__*/ _jsx(CourseHeaderPrice, {
                                price: priceData,
                                isFree: isFree
                            })
                        ]
                    }),
                    languageSelect && /*#__PURE__*/ _jsx(RightSection, {
                        children: /*#__PURE__*/ _jsx(LanguageSelectWrapper, {
                            children: languageSelect.isMulti ? /*#__PURE__*/ _jsx(ResponsiveSelect, {
                                selectionMode: "single",
                                selectedKeys: languageSelectedKeys,
                                onSelectionChange: handleLanguageSelectionChange,
                                "aria-label": "강의 언어 선택",
                                size: "sm",
                                variant: "bordered",
                                "data-testid": languageSelect.testId,
                                children: languageSelect.options.map((option)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: option.value,
                                        textValue: option.label,
                                        children: option.label
                                    }, option.value))
                            }) : /*#__PURE__*/ _jsx(LanguageValue, {
                                "data-testid": languageSelect.testId,
                                children: selectedLanguageLabel
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(MetaRow, {
                children: [
                    /*#__PURE__*/ _jsxs(LeftSection, {
                        children: [
                            /*#__PURE__*/ _jsx(AuthorWrapper, {
                                onClick: onAuthorClick,
                                children: /*#__PURE__*/ _jsx(AuthorDisplay, {
                                    author: authorForDisplay,
                                    size: "sm",
                                    hideAvatar: false,
                                    layout: "inline"
                                })
                            }),
                            /*#__PURE__*/ _jsx(CourseHeaderProgress, {
                                isEnrolled: isEnrolled,
                                isCompleted: isCompleted,
                                progressRate: courseData.progressRate
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(RightSection, {
                        children: /*#__PURE__*/ _jsx(CourseHeaderActions, {
                            courseId: courseData.id,
                            courseTitle: courseData.title,
                            isEnrolled: isEnrolled,
                            isFree: isFree,
                            showCancelButton: showCancelButton,
                            onEnrollmentSuccess: onEnrollmentSuccess,
                            onCancelEnrollment: onCancelEnrollment,
                            currentEnrollmentId: currentEnrollmentId,
                            isInstructor: isInstructor,
                            onEditClick: onEditClick
                        })
                    })
                ]
            })
        ]
    });
};
